import { useRef } from 'react';
import { addDays, differenceInDays } from 'date-fns';
import api from 'api';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { getPasswordRequirementsString } from 'lib/auth';
import { getFormattedDate } from 'lib/common';
import { Box } from 'components/common/box';
import { FormGroup, Password, Relevant } from 'components/common/form';
import { Alert, ModalButtonWithForm } from 'components/common';

import type { FormApi } from 'informed';

type FormValues = {
  confirmNewPassword: string;
  currentPassword: string;
  newPassword: string;
};

const ProfileAuthPassword = () => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();
  const {
    messages,
    pageState: { user },
    serverConfig
  } = useReduxState();

  const formApi = useRef<FormApi<FormValues>>(undefined);

  const handleNewPasswordChange = (value) => {
    const confirmNewPassword = formApi.current?.getValue('confirmNewPassword');
    if (confirmNewPassword) {
      const error =
        value !== confirmNewPassword
          ? messages.authPasswordMatchError
          : undefined;
      formApi.current?.setError('confirmNewPassword', error);
    }
  };

  const submit = async (values) => {
    const response = await api.post('/auth/local/password', values);

    if (response.data && response.data.success) {
      dispatch.updatePageState({
        user: { ...user, passwordSet: response.data.passwordSet }
      });
      dispatch.addNotice({
        text: user?.passwordSet
          ? messages.profileChangePasswordSuccess
          : messages.authSetPasswordSuccess,
        type: 'success'
      });
    } else {
      let text = messages.commonError;
      if (
        response.data &&
        response.data.error === 'prevent_five_last_passwords'
      ) {
        text = messages.authPasswordPreventFiveLastError;
      } else if (response.data && response.data.error === 'password_invalid') {
        text = messages.authCurrentPasswordInvalid;
      }
      await dispatch.handleErrorDispatch(null, text);
      return false;
    }
  };

  const validateConfirmPassword = (value) => {
    const newPassword = formApi.current?.getValue('newPassword');
    return value !== newPassword ? messages.authPasswordMatchError : undefined;
  };

  const expirationTimeInDays = serverConfig.passwordExpirationTimeInDays;
  const expirationDate = expirationTimeInDays
    ? addDays(Date.parse(user?.passwordSet as string), expirationTimeInDays)
    : null;
  const daysUntilExpiration = expirationTimeInDays
    ? differenceInDays(expirationDate, dateNow)
    : null;

  return (
    <div className="password">
      <Box
        title={
          user?.passwordSet
            ? messages.profileChangePassword
            : messages.authSetPassword
        }
      >
        {user?.passwordSet ? (
          <Alert
            text={`${messages.profilePasswordUpdated} ${getFormattedDate({
              date: user.passwordSet
            })}.`}
          />
        ) : (
          <Alert text={messages.profilePasswordNotSet} />
        )}

        {expirationTimeInDays ? (
          <Alert
            style={{ marginTop: 10 }}
            text={
              expirationDate >= new Date()
                ? messages.profilePasswordExpires +
                  ` ${daysUntilExpiration} ${messages.profilePasswordExpiresDays}.`
                : messages.authPasswordExpired
            }
            type={daysUntilExpiration > 14 ? 'info' : 'error-light'}
          />
        ) : null}

        <div className="change-password">
          <ModalButtonWithForm
            buttonIcon="key"
            buttonText={
              user?.passwordSet
                ? messages.profileChangePassword
                : messages.authSetPassword
            }
            modalActionCategory="success"
            modalActionText={
              user?.passwordSet
                ? messages.commonSaveChanges
                : messages.authSetPassword
            }
            modalTitle={
              user?.passwordSet
                ? messages.profileChangePassword
                : messages.authSetPassword
            }
            onSubmit={submit}
            pristineIsValid={false}
            setFormApi={(form: FormApi<FormValues>) => (formApi.current = form)}
          >
            {(formState) => (
              <>
                <Relevant when={() => Boolean(user?.passwordSet)}>
                  <FormGroup
                    htmlFor="profile-current-password"
                    label={messages.authCurrentPassword}
                  >
                    <Password
                      field="currentPassword"
                      id="profile-current-password"
                      style={{ maxWidth: 280 }}
                    />
                  </FormGroup>
                </Relevant>

                <FormGroup
                  htmlFor="profile-new-password"
                  label={messages.authNewPassword}
                  labelHelpText={getPasswordRequirementsString({
                    messages,
                    serverConfig
                  })}
                >
                  <Password
                    field="newPassword"
                    formApi={formApi.current}
                    id="profile-new-password"
                    onChange={handleNewPasswordChange}
                    style={{ maxWidth: 280 }}
                    userEmailAddress={user ? user.email : ''}
                    userUsername={user ? user.username : ''}
                    validateNewPassword={true}
                  />
                </FormGroup>

                <FormGroup
                  htmlFor="profile-confirm-new-password"
                  label={messages.authConfirmPassword}
                >
                  <Password
                    field="confirmNewPassword"
                    id="profile-confirm-new-password"
                    style={{ maxWidth: 280 }}
                    validateAdditional={validateConfirmPassword}
                  />
                </FormGroup>
              </>
            )}
          </ModalButtonWithForm>
        </div>
      </Box>

      <style jsx>{`
        .change-password {
          margin-top: 15px;
        }
      `}</style>
    </div>
  );
};

export default ProfileAuthPassword;
